import React from "react";
import { Table, Dimmer, Loader, Label, Icon, Button } from "semantic-ui-react";
import {
  STATUS_INFO,
  reqTypeToKorean,
  formatPrice,
  decodeEntities
} from "../../../common/utils/format";

const EMPTY_STYLE = { color: "#bbb" };

const timestampToDate = dt =>
  dt ? new Date(dt).toLocaleDateString() : "";

const renderStatus = s => {
  const info = STATUS_INFO[s];
  if (!info) return s || "-";
  return (
    <Label color={info.color} size="small">
      {info.text}
    </Label>
  );
};

const yearMonth = s =>
  s && s.length >= 6 ? `${s.substring(0, 4)}.${s.substring(4, 6)}` : s || "-";

const renderPrice = p =>
  p != null ? (
    <span style={{ color: "#2185d0", fontWeight: 600 }}>
      {formatPrice(p)}
      <span style={{ color: "#999", marginLeft: 2, fontWeight: 400 }}>원</span>
    </span>
  ) : (
    <span style={EMPTY_STYLE}>-</span>
  );

const renderEmpty = v => (v ? v : <span style={EMPTY_STYLE}>-</span>);

const splitNewline = s =>
  s
    ? s.split("\n").map((line, i) => (
        <span key={i}>
          {line}
          <br />
        </span>
      ))
    : <span style={EMPTY_STYLE}>-</span>;

const renderUrl = url =>
  url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ wordBreak: "break-all" }}
      onClick={e => e.stopPropagation()}
    >
      <Icon name="external alternate" size="small" />
      {url}
    </a>
  ) : (
    <span style={EMPTY_STYLE}>-</span>
  );

// 한 row 의 속성 정의: 라벨 + (book) → cell content.
const ROWS = [
  { label: "도서명", icon: "book", render: b => renderEmpty(decodeEntities(b.bookName)), strong: true },
  { label: "유형", icon: "tag", render: b => renderEmpty(b.bookType) },
  { label: "URL", icon: "linkify", render: b => renderUrl(b.reqUrl) },
  { label: "배송지", icon: "shipping fast", render: b => renderEmpty(decodeEntities(b.address)) },
  { label: "이름", icon: "user", render: b => renderEmpty(b.mbrName) },
  { label: "가격", icon: "won sign", render: b => renderPrice(b.price) },
  { label: "개인/공용", icon: "users", render: b => reqTypeToKorean(b.reqType) || b.reqType || "-" },
  { label: "신청년월", icon: "calendar", render: b => yearMonth(b.reqYearMonth) },
  { label: "등록일시", icon: "clock", render: b => renderEmpty(timestampToDate(b.regDt)) },
  { label: "상태", icon: "flag", render: b => renderStatus(b.status) },
  { label: "비고", icon: "sticky note", render: b => splitNewline(decodeEntities(b.etc)), scrollable: true },
  { label: "반려사유", icon: "ban", render: b => splitNewline(decodeEntities(b.rejectReason)), scrollable: true }
];

const SCROLL_CELL = {
  maxHeight: 100,
  overflowY: "auto",
  wordBreak: "break-all"
};

/**
 * 단권 → books=[book] (1 컬럼)
 * 묶음 → books=batchBooks (N 컬럼, 현재 책이 첫 컬럼)
 *
 * row 단위 셀이라 책별 컬럼 간 높이 자동 정렬됨.
 * 도서명 셀 클릭 시 그 책 상세로 이동.
 */
// 책별 인라인 액션.
// admin: REQUEST → [승인][반려],  APPROVAL → [수령등록],  그 외 → "처리 완료"
// 본인:  APPROVAL → [수령등록],   REQUEST/그 외 → 상태 라벨만 (CANCLE 은 묶음 전체 취소 버튼으로)
const PerBookActions = ({ book, onAction, disabled, isAdmin }) => {
  const s = book.status;

  if (isAdmin === "Y") {
    if (s === "REQUEST") {
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          <Button size="tiny" color="blue" disabled={disabled}
                  onClick={() => onAction(book, "APPROVAL")}>승인</Button>
          <Button size="tiny" color="red" disabled={disabled}
                  onClick={() => onAction(book, "REJECT")}>반려</Button>
        </div>
      );
    }
    if (s === "APPROVAL") {
      return (
        <Button size="tiny" color="green" disabled={disabled}
                onClick={() => onAction(book, "RECEIPT")}>수령등록</Button>
      );
    }
    return <span style={{ color: "#999", fontSize: "0.85em" }}>처리 완료</span>;
  }

  // 일반 사용자(본인 신청)
  if (s === "APPROVAL") {
    return (
      <Button size="tiny" color="green" disabled={disabled}
              onClick={() => onAction(book, "RECEIPT")}>수령등록</Button>
    );
  }
  return <span style={{ color: "#999", fontSize: "0.85em" }}>—</span>;
};

const BookDetailTable = ({ books, currentOrdNum, onPickBook, isAdmin, onPerBookAction, perBookDisabled }) => {
  if (!books || books.length === 0) {
    return (
      <Dimmer active inverted>
        <Loader>Loading...</Loader>
      </Dimmer>
    );
  }

  // 각 책 컬럼 폭 (% 단위, 합 = 100 - 좌측 라벨 폭)
  // 좌측 라벨 = 20%. 나머지 80% 를 책 수로 등분.
  const labelWidthPct = 20;
  const bookColPct = (100 - labelWidthPct) / books.length;

  const isBatch = books.length > 1;
  const totalPrice = books.reduce(
    (s, b) => s + (Number(b.price) || 0),
    0
  );

  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid #e0e1e2",
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(34,36,38,.08)"
      }}
    >
      <Table
        celled
        compact
        unstackable
        style={{
          minWidth: Math.max(books.length * 240, 480),
          tableLayout: "fixed",
          margin: 0,
          border: "none",
          borderRadius: 8,
          overflow: "hidden"
        }}
      >
        <colgroup>
          <col style={{ width: `${labelWidthPct}%` }} />
          {books.map((_, idx) => (
            <col key={idx} style={{ width: `${bookColPct}%` }} />
          ))}
        </colgroup>

        {isBatch ? (
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                style={{ background: "#f3f4f6", whiteSpace: "nowrap" }}
              >
                속성
              </Table.HeaderCell>
              {books.map((book, idx) => (
                <Table.HeaderCell
                  key={book.ordNum || idx}
                  textAlign="center"
                  style={{ background: "#f3f4f6", whiteSpace: "nowrap" }}
                >
                  책 {idx + 1}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
        ) : null}

        <Table.Body>
          {ROWS.map(row => (
            <Table.Row key={row.label}>
              <Table.Cell
                style={{
                  background: "#f9fafb",
                  fontWeight: 600,
                  color: "#555",
                  whiteSpace: "nowrap",
                  verticalAlign: "top"
                }}
              >
                {row.icon ? (
                  <Icon name={row.icon} style={{ color: "#9ca3af" }} />
                ) : null}
                {row.label}
              </Table.Cell>
              {books.map(book => {
                const isCurrent = book.ordNum === currentOrdNum;
                const clickable =
                  row.label === "도서명" && !isCurrent && onPickBook;
                return (
                  <Table.Cell
                    key={book.ordNum + row.label}
                    style={{
                      ...(row.scrollable ? SCROLL_CELL : {}),
                      ...(row.strong ? { fontWeight: 600 } : {}),
                      cursor: clickable ? "pointer" : undefined,
                      wordBreak: "break-word",
                      verticalAlign: "top"
                    }}
                    onClick={
                      clickable ? () => onPickBook(book.ordNum) : undefined
                    }
                    title={clickable ? "이 책의 상세로 이동" : undefined}
                  >
                    {row.render(book)}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          ))}

          {/* 묶음(책 2권 이상) 일 때만 책별 인라인 처리 행 노출.
              admin: REQUEST/APPROVAL 책에 처리 액션.
              본인:  APPROVAL 책에 [수령등록]. (REQUEST 책별 취소는 묶음 전체 취소 버튼으로) */}
          {onPerBookAction && books.length > 1 ? (
            <Table.Row>
              <Table.Cell
                style={{
                  background: "#f9fafb",
                  fontWeight: 600,
                  color: "#555",
                  whiteSpace: "nowrap",
                  verticalAlign: "top"
                }}
              >
                <Icon name="cogs" style={{ color: "#9ca3af" }} />
                처리
              </Table.Cell>
              {books.map(book => (
                <Table.Cell key={"action-" + book.ordNum} style={{ verticalAlign: "top" }}>
                  <PerBookActions
                    book={book}
                    onAction={onPerBookAction}
                    disabled={perBookDisabled}
                    isAdmin={isAdmin}
                  />
                </Table.Cell>
              ))}
            </Table.Row>
          ) : null}
        </Table.Body>
      </Table>
      {isBatch ? (
        <div
          style={{
            padding: "0.8em 1.2em",
            textAlign: "right",
            background: "#f9fafb",
            borderTop: "1px solid #e0e1e2",
            fontSize: "1.05em"
          }}
        >
          <span style={{ color: "#555", marginRight: 8 }}>
            총 {books.length}권 · 합계
          </span>
          <span style={{ color: "#2185d0", fontWeight: 700, fontSize: "1.15em" }}>
            {formatPrice(totalPrice)}
          </span>
          <span style={{ color: "#999", marginLeft: 2 }}>원</span>
        </div>
      ) : null}
    </div>
  );
};

export default BookDetailTable;
